import {
  Box,
  ClickAwayListener,
  css,
  Divider,
  Modal,
  Stack,
  Typography,
  useMediaQuery
} from '@mui/material'
import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'
import ChevronDown from 'assets/icons/nucleo/18px_chevron-down.svg'
import RadioCheck from 'assets/icons/nucleo/18px_radio-checked.svg'
import RadioUncheck from 'assets/icons/nucleo/18px_radio-unchecked.svg'
import { minWidthIcon } from 'components/Layout/Header/Header.style'
import { Button, ButtonVariant } from 'components/common/Button'
import { InputErrorLabel } from 'components/common/TextInput'
import { theme, themeUtils } from 'styles/theme'
import { Option, OptionsContainer, StyledButton } from './SelectV2.style'

interface SelectProps<T extends string> {
  label: string
  value?: T
  title?: string
  options: { value: T; label: string }[]
  onSave: (value: T) => void
  fullWidth?: boolean
  saveOnChange?: boolean
  errorMessage?: string
}

export const Select = <T extends string>({
  label,
  options,
  value,
  onSave,
  title,
  fullWidth,
  saveOnChange,
  errorMessage
}: SelectProps<T>) => {
  const { t } = useTranslation('common')
  const [open, setOpen] = useState(false)
  const isMobile = useMediaQuery(themeUtils.maxWidth.md)
  const [temporaryValue, setTemporaryValue] = useState<T | undefined>(value)

  useEffect(() => {
    setTemporaryValue(value)
  }, [value])

  const handleOnChange = (newValue: T) => {
    setTemporaryValue(newValue)
    if (saveOnChange) {
      onSave(newValue)
      setOpen(false)
    }
  }

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box position="relative" m={0}>
        {title && (
          <Typography variant="subtitle2" fontWeight={600}>
            {title}
          </Typography>
        )}
        <StyledButton
          label={label}
          fullWidth={fullWidth}
          rightIcon={<ChevronDown css={minWidthIcon} />}
          variant={ButtonVariant.Quaternary}
          onClick={() => setOpen((prev) => !prev)}
          type="button"
          ellipsis
          css={
            errorMessage &&
            css`
              border: 1px solid ${theme.palette.border.focused.error};
            `
          }
        />
        {open && (
          <Modal
            open={open}
            onClose={() => setOpen(false)}
            hideBackdrop={!isMobile}
            disablePortal={!isMobile}
            disableEnforceFocus={!isMobile}
            sx={{
              position: isMobile ? 'fixed' : 'relative'
            }}
          >
            <OptionsContainer $fullWidth={fullWidth}>
              {options.map((item) => (
                <Option
                  key={item.value}
                  onClick={() => handleOnChange(item.value)}
                >
                  {item.value === temporaryValue ? (
                    <RadioCheck css={minWidthIcon} />
                  ) : (
                    <RadioUncheck css={minWidthIcon} />
                  )}
                  <Typography variant="body2" fontWeight={600}>
                    {item.label}
                  </Typography>
                </Option>
              ))}
              {!saveOnChange && (
                <>
                  <Divider />
                  <Stack p={1.5} alignItems="flex-end">
                    <Button
                      variant={ButtonVariant.Primary}
                      label={t('eventPage.saveFavorite')}
                      onClick={() => {
                        temporaryValue && onSave(temporaryValue)
                        setOpen(false)
                      }}
                    />
                  </Stack>
                </>
              )}
            </OptionsContainer>
          </Modal>
        )}
        {errorMessage && <InputErrorLabel>{errorMessage}</InputErrorLabel>}
      </Box>
    </ClickAwayListener>
  )
}
